import { LinearProgress, Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { List, ListItemButton, Text } from "@src/appV2/redesign/system/ui-components";
import { useEffect, useState } from "react";

export interface TransferShiftQuizQuestion {
  question: string;
  correctAnswer: string;
  wrongAnswers: string[];
  onWrongMessage: string;
  onCorrectMessage: string;
}

export interface TransferShiftQuizQuestionCardProps {
  quizQuestion: TransferShiftQuizQuestion;
  questionNumber: number;
  totalQuestions: number;
  onCorrectAnswer?: () => void;
}

function combineAnswers(correctAnswer: string, wrongAnswers: string[]) {
  return [
    ...wrongAnswers.map((element) => ({ answer: element, correct: false })),
    { answer: correctAnswer, correct: true },
  ];
}

function getShuffledArray<T>(array: T[]): T[] {
  return array
    .map((element) => ({ sort: Math.random(), value: element }))
    .sort((element1, element2) => element1.sort - element2.sort)
    .map((element) => element.value);
}

export function TransferShiftQuizQuestionCard(props: TransferShiftQuizQuestionCardProps) {
  const { quizQuestion, questionNumber, totalQuestions, onCorrectAnswer } = props;
  const { question, correctAnswer, wrongAnswers, onWrongMessage, onCorrectMessage } = quizQuestion;

  const [answerState, setAnswerState] = useState<"question" | "wrongAnswer" | "correctAnswer">(
    "question"
  );

  const [allAnswers, setAllAnswers] = useState(
    getShuffledArray(combineAnswers(correctAnswer, wrongAnswers))
  );
  useEffect(() => {
    setAllAnswers(getShuffledArray(combineAnswers(correctAnswer, wrongAnswers)));
  }, [correctAnswer, wrongAnswers]);

  return (
    <Stack spacing={5} alignItems="center">
      <Text>{`Question ${questionNumber} of ${totalQuestions}`}</Text>
      <LinearProgress
        sx={{ width: "100%" }}
        variant="determinate"
        value={(100 * questionNumber) / totalQuestions}
        color={answerState === "wrongAnswer" ? "error" : "primary"}
      />
      {answerState === "question" ? (
        <Stack spacing={2} sx={{ width: "100%" }}>
          <Text variant="h6">{question}</Text>
          <List>
            {allAnswers.map(({ answer, correct }) => (
              <ListItemButton
                key={`answer-${answer.slice(0, 5)}/${answer.length}`}
                sx={{ padding: 4 }}
                onClick={() => {
                  if (correct) {
                    setAnswerState("correctAnswer");
                  } else {
                    setAnswerState("wrongAnswer");
                  }
                }}
              >
                <Text sx={{ width: "100%" }}>{answer}</Text>
              </ListItemButton>
            ))}
          </List>
        </Stack>
      ) : (
        <Stack spacing={12}>
          <Text>{answerState === "correctAnswer" ? onCorrectMessage : onWrongMessage}</Text>
          <Button
            fullWidth
            size="large"
            variant={answerState === "wrongAnswer" ? "outlined" : "contained"}
            onClick={() => {
              if (answerState === "correctAnswer") {
                onCorrectAnswer?.();
              } else {
                setAllAnswers(getShuffledArray(allAnswers));
              }

              setAnswerState("question");
            }}
          >
            {answerState === "correctAnswer" ? "Next" : "Try Again"}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
